import { ImmapApi } from '../../../api/ImmapApi';
import { AssetsApi } from '../../../api/assetsApi';
import { MappingApi } from '../../../api/mappingApi';
import { ProcessApi } from '../../../api/processApi';
import { TemplatesApi } from '../../../api/templatesApi';
import { BatchDeterminationContext } from '../../../contexts/BatchDeterminationContext';
import { FileContext } from '../../../contexts/FileContext';
import { AdvancedOptions, MappingInfo } from '../../../interfaces/Mapping';
import { SnackbarParameters } from '../../../interfaces/Snackbar';
import { Template } from '../../../interfaces/Templates';
import SovosLogo from '../../../shared/assets/sovos-logo.png';
import withSnackbar from '../../../shared/components/withSnackbar';
import {
	DEFAULT_ADVANCED_OPTIONS,
	DEFAULT_TEMPLATE_INFO,
	IMPORT_PROCESS_STATE,
	INITIAL_STATE,
	PREVIEW_FILE_ROWS,
	TEMPLATE_COLUMNS_ERROR_MESSAGE,
	TEMPLATE_COMPATIBILITY_ERROR_MESSAGE,
} from '../../../shared/constants/ImportPageConstants';
import { CUSTOM_FORMAT, CUSTOM_IMPORT } from '../../../shared/constants/constants';
import {
	isFileIsCompatible,
	isTwoAdvancedOptionsEqual,
	isTwoMappingColumnsEqual,
} from '../../../shared/helpers/importProcessSlidingPanelHelpers';
import ImportPageContent from './ImportPageContent';
import GenericDialog from './dialogs/GenericDialog';
import { SovosButton, SovosDivider, SovosIconButton } from 'mosaic-react';
import { Close } from 'mosaic-react-icons';
import { useContext, useEffect, useState } from 'react';

interface ImportProcessProps {
	showSnackbar: (value: SnackbarParameters) => void;
}

const customImportTemplate = {
	id: '',
	name: CUSTOM_IMPORT,
};

const ImportPage = (props: ImportProcessProps) => {
	const { showSnackbar } = props;
	const { getImportData, selectedTransaction, forceRefreshingTemplateTable, onClose, setSelectedTransaction } =
		useContext(BatchDeterminationContext);

	const [processStatus, setProcessStatus] = useState<number>(IMPORT_PROCESS_STATE.UPLOADING);
	const [isPreparingMapping, setIsPreparingMapping] = useState<boolean>(false);
	const [checkboxSelected, setCheckboxSelected] = useState<boolean>(false);
	const [allTemplatesFetched, setAllTemplatesFetched] = useState<Template[]>([]);
	const [selectedCustomTemplate, setSelectedCustomTemplate] = useState<Template>(INITIAL_STATE.selectedCustomTemplate);
	const [globalColumnsToHide, setGlobalColumnsToHide] = useState<MappingInfo[]>([]);
	const [globalMappingData, setGlobalMappingData] = useState<MappingInfo[]>([]);
	const [isAudit, setIsAudit] = useState<boolean>(false);
	const [haveExtraRows, setHaveExtraRows] = useState<boolean>(false);
	const [isDisableBeginProcessingButton, setDisableBeginProcessingButton] = useState<boolean>(true);
	const [isFetchingTargets, setFetchingTargets] = useState<boolean>(false);
	const [columnsHeaders, setColumnsHeaders] = useState<MappingInfo[]>([]);
	const [isEditGlobalTemplate, setIsEditGlobalTemplate] = useState<boolean>(false);
	const [selectedTemplate, setSelectedTemplate] = useState<Template>(INITIAL_STATE.selectedTemplate);
	const [advancedOptions, setAdvancedOptions] = useState<AdvancedOptions>(INITIAL_STATE.advancedOptions);
	const [mappedColumns, setMappedColumns] = useState<MappingInfo[]>([]);
	const [processId, setProcessId] = useState<string>('');
	const [isRemoveImportDialogOpen, setIsRemoveImportDialogOpen] = useState<boolean>(false);
	const [isAuditDialogOpen, setIsAuditDialogOpen] = useState<boolean>(false);
	const [disableAuditButton, setDisableAuditButton] = useState<boolean>(true);
	const [importTemplate, setImportTemplate] = useState<Template>(customImportTemplate);
	const { selectedFiles, deleteFile } = useContext(FileContext);
	const fileId = selectedFiles[0]?.id || selectedTransaction?.fileGuid || '';
	const fileName = selectedFiles[0]?.name || selectedTransaction?.fileName || '';
	const transactionDataExists = selectedTransaction && Object.keys(selectedTransaction).length;

	const getMapAndPremapGlobalTemplateEdit = async (): Promise<void> => {
		setIsPreparingMapping(true);
		const premapInfo = await AssetsApi.getPreMapInfo(fileId, selectedTemplate.ruleType);
		let mappingInfo = await getMappingTemplateInfo();

		if (premapInfo && mappingInfo && isFileIsCompatible(mappingInfo, premapInfo)) {
			setIsPreparingMapping(false);
			mappingInfo = getMappingGlobalTemplateInfo(premapInfo, mappingInfo);
			const { calculationType, dateFormat, transactionType, ruleType } = selectedTemplate;
			setAdvancedOptions({
				...advancedOptions,
				calculationType: calculationType ?? DEFAULT_ADVANCED_OPTIONS.calculationType,
				dateFormat: dateFormat ?? DEFAULT_ADVANCED_OPTIONS.dateFormat,
				transactionType: transactionType ?? DEFAULT_ADVANCED_OPTIONS.transactionType,
				ruleType: ruleType ?? DEFAULT_ADVANCED_OPTIONS.ruleType,
			});
			mappingInfo !== undefined ? setColumns([...mappingInfo]) : setColumns([]);
			setProcessStatus(IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT);
		} else if (!premapInfo) {
			showSnackbar({
				message: TEMPLATE_COLUMNS_ERROR_MESSAGE,
				variant: 'error',
			});
			setIsPreparingMapping(false);
		} else {
			setIsPreparingMapping(false);
			showSnackbar({
				message: TEMPLATE_COMPATIBILITY_ERROR_MESSAGE,
				variant: 'error',
			});
		}
	};

	const handleGlobalTemplateEditSelect = (template: Template): void => {
		setSelectedTemplate(template);
		setIsEditGlobalTemplate(true);
	};

	const checkAndGenerateColumnId = (columns: MappingInfo[]): number => {
		const id = Math.floor(Math.random() * 999) + 1;
		if (columns.some(column => column.id === id)) {
			return checkAndGenerateColumnId(columns);
		} else {
			return id;
		}
	};

	const setColumns = (columns: MappingInfo[]): void => {
		columns.forEach(column => {
			if (!column.id) {
				column.id = checkAndGenerateColumnId(columns);
			}
		});
		setMappedColumns([...columns]);
	};

	const createProcess = async (profileId: string): Promise<void> => {
		try {
			const processResponse = await ProcessApi.createProcess(fileName, fileId, profileId);
			if (processResponse.status === 202) {
				const newProcessId = processResponse.data;
				setProcessId(newProcessId);
				sessionStorage.setItem('processId', newProcessId);

				await ImmapApi.createProcess(newProcessId)
					.then(response => {
						if (response.status === 202) {
							getImportData();
							forceRefreshingTemplateTable();
						} else if (response.status === 400 && response.data.message === 'Malformed input file') {
							showSnackbar({
								message: 'Make sure that your file has the proper content before trying to process',
								variant: 'warning',
							});
							setTimeout(() => onClose(), 5000);
							deleteFile(fileId);
							ProcessApi.deleteProcess(newProcessId);
							setProcessStatus(IMPORT_PROCESS_STATE.ERROR);
						} else if (
							response.status === 400 &&
							response.data.errors &&
							response.data.errors[0].subcode === 'IMMAP-061'
						) {
							showSnackbar({
								message: response.data.errors[0].message,
								variant: 'error',
							});
							getImportData();
							forceRefreshingTemplateTable();
							setProcessStatus(IMPORT_PROCESS_STATE.MAX_LIMIT_EXCEEDED);
						} else {
							if (selectedTemplate.id !== CUSTOM_FORMAT) deleteFile(fileId);
							ProcessApi.deleteProcess(newProcessId);
							setProcessStatus(IMPORT_PROCESS_STATE.ERROR);
						}
					})
					.catch(error => {
						console.log('Error creating process');
					});
			} else {
				if (selectedTemplate.id !== CUSTOM_FORMAT) deleteFile(fileId);
				setProcessStatus(IMPORT_PROCESS_STATE.ERROR);
			}
		} catch (error) {
			if (selectedTemplate.id !== CUSTOM_FORMAT) deleteFile(fileId);
			setProcessStatus(IMPORT_PROCESS_STATE.ERROR);
		}
	};

	const beginProcessing = async (selectedTemplateId: string): Promise<void> => {
		setProcessStatus(IMPORT_PROCESS_STATE.PROCESSING);
		await createProcess(selectedTemplateId);
	};

	const getMappingGlobalTemplateInfo = (premapInfo: MappingInfo[], mappingInfo: MappingInfo[]): MappingInfo[] => {
		const columnsCards: MappingInfo[] = [];
		premapInfo.forEach(function (columnPreMap) {
			const data = mappingInfo.filter(column => column.sourceField === columnPreMap.sourceField);
			data[0]?.sourceField === columnPreMap.sourceField && data[0]?.targetField !== ''
				? columnsCards.push({ ...data[0] })
				: columnsCards.push(columnPreMap);
		});
		const columnCardsToShow = columnsCards.filter(column => column.targetField !== '');
		const columnCardsToHide = columnsCards.filter(column => column.targetField === '');
		setGlobalMappingData(columnsCards);
		setGlobalColumnsToHide(columnCardsToHide);
		setColumnsHeaders(
			columnsCards.map(columnHeader => ({
				...columnHeader,
				disabled: columnCardsToShow.some(column => column.sourceField === columnHeader.sourceField),
			}))
		);
		if (columnCardsToShow.length === 0) columnCardsToShow.unshift({ id: 0, sourceField: '', targetField: '' });
		return columnCardsToShow;
	};

	const getMappingTemplateInfo = async (): Promise<MappingInfo[]> => {
		if (selectedTemplate.id !== CUSTOM_FORMAT) {
			const mappingId = selectedTemplate?.mapping?.id;

			const mappingData = await MappingApi.getMappingTemplateInfo(mappingId);
			setGlobalMappingData(mappingData);
			const columnCardsToHide = mappingData.filter(column => column.targetField !== '');
			setGlobalColumnsToHide(columnCardsToHide);
			return mappingData;
		}
		return [];
	};

	const handleUploadStepClick = async (): Promise<void> => {
		setIsPreparingMapping(true);
		let premapInfo: MappingInfo[] | null = [];
		let mappingInfo: MappingInfo[] = [];

		await AssetsApi.getPreMapInfo(fileId, selectedTemplate.ruleType)
			.then(async response => {
				premapInfo = response;

				if (selectedTemplate.id !== CUSTOM_FORMAT) {
					await MappingApi.getMappingTemplateInfo(selectedTemplate?.mapping?.id)
						.then(response => {
							mappingInfo = response;
						})
						.catch(error => {
							console.error('Error fetching mapping template info');
						});
				}
			})
			.catch(() => (premapInfo = null));

		const isFileCompatible = premapInfo && mappingInfo && isFileIsCompatible(premapInfo, mappingInfo);
		if (selectedTemplate.id === CUSTOM_FORMAT && premapInfo) {
			setProcessStatus(IMPORT_PROCESS_STATE.MATCHING);
		} else if (!premapInfo) {
			showSnackbar({
				message: TEMPLATE_COLUMNS_ERROR_MESSAGE,
				variant: 'error',
			});
			setIsPreparingMapping(false);
		} else if (selectedTemplate.id !== CUSTOM_FORMAT && checkboxSelected) {
			const { calculationType, dateFormat, transactionType, ruleType } = selectedTemplate;
			setAdvancedOptions({
				...advancedOptions,
				calculationType: calculationType || 5,
				dateFormat: dateFormat || 'MM/dd/yyyy',
				transactionType: transactionType || 1,
				ruleType: ruleType || '',
			});
			setIsPreparingMapping(false);

			if (isFileCompatible) {
				premapInfo?.forEach((preMapping: MappingInfo) => {
					mappingInfo.forEach((mapping: MappingInfo) => {
						if (mapping.sourceField === preMapping.sourceField && mapping.targetField !== '') {
							preMapping.targetField = mapping.targetField;
						}
					});
				});
				setColumns([...premapInfo]);
				setProcessStatus(IMPORT_PROCESS_STATE.MATCHING);
			} else {
				showSnackbar({
					message: TEMPLATE_COMPATIBILITY_ERROR_MESSAGE,
					variant: 'error',
				});
			}
		} else if (selectedTemplate.id !== CUSTOM_FORMAT && !checkboxSelected) {
			if (isFileCompatible) {
				beginProcessing(selectedTemplate.id);
			} else {
				showSnackbar({
					message: TEMPLATE_COMPATIBILITY_ERROR_MESSAGE,
					variant: 'error',
				});
				setIsPreparingMapping(false);
			}
		} else {
			beginProcessing(selectedTemplate.id);
		}
		setIsPreparingMapping(false);
	};

	const handleAddNewTransaction = (): void => {
		setSelectedTemplate(INITIAL_STATE.selectedTemplate);
		setProcessStatus(INITIAL_STATE.processStatus);
		setProcessId(INITIAL_STATE.processId);
		setColumns(INITIAL_STATE.mappedColumns);
		setSelectedCustomTemplate(INITIAL_STATE.selectedCustomTemplate);
		setAdvancedOptions(INITIAL_STATE.advancedOptions);
		setAllTemplatesFetched(INITIAL_STATE.allTemplatesFetched);
		setCheckboxSelected(false);
		setIsPreparingMapping(false);
		setColumnsHeaders([]);
	};

	const handleMatchingStepClick = async (): Promise<void> => {
		setDisableBeginProcessingButton(true);
		const customTemplateInfoSelected = allTemplatesFetched.find(template => template.id === selectedCustomTemplate.id);
		const isTemplateCustomFormat = Boolean(selectedTemplate.id === CUSTOM_FORMAT);

		let mappedColumnsFetched: MappingInfo[] = [];
		if (customTemplateInfoSelected?.id !== '') {
			/* fetch mappedColumns then compare with current mapped columns */
			mappedColumnsFetched = await MappingApi.getMappingTemplateInfo(customTemplateInfoSelected?.mapping?.id);
		} else if (selectedTemplate && !isTemplateCustomFormat && selectedCustomTemplate.id === '') {
			mappedColumnsFetched = await MappingApi.getMappingTemplateInfo(selectedTemplate?.mapping?.id);
		}

		setProcessStatus(IMPORT_PROCESS_STATE.PROCESSING);

		if (
			!isTwoMappingColumnsEqual(mappedColumns, mappedColumnsFetched) ||
			(isTemplateCustomFormat && !isTwoAdvancedOptionsEqual(customTemplateInfoSelected!, advancedOptions))
		) {
			// Create new process using temporary template
			setSelectedCustomTemplate(customImportTemplate);
			try {
				const scope = 'T';
				const mappingResponse = await MappingApi.saveMappedColumns(
					advancedOptions.calculationType,
					mappedColumns,
					scope
				);

				TemplatesApi.createTemplate(
					{ ...DEFAULT_TEMPLATE_INFO, scope, version: 0 },
					advancedOptions,
					mappingResponse.data.id
				).then(async response => {
					if (response.status === 201) {
						const newProfileId = response.data.id;
						await createProcess(newProfileId);
					} else {
						if (selectedTemplate.id !== CUSTOM_FORMAT) deleteFile(fileId);
						setProcessStatus(IMPORT_PROCESS_STATE.ERROR);
					}
				});
			} catch (error) {
				if (selectedTemplate.id !== CUSTOM_FORMAT) deleteFile(fileId);
				setProcessStatus(IMPORT_PROCESS_STATE.ERROR);
			}
		} else {
			let templateId = '';
			if (selectedCustomTemplate.id === '' && selectedTemplate.id !== 'Custom Format') {
				templateId = selectedTemplate.id;
			} else {
				templateId = selectedCustomTemplate.id;
			}
			await createProcess(templateId);
		}
		setDisableBeginProcessingButton(false);
	};

	const handleRetryProcessClick = (): void => {
		ProcessApi.getProcess(processId).then(async response => {
			const isAudit = response.isAudit;
			await ImmapApi.createProcess(processId, isAudit);
		});
	};

	const sendToAudit = () => {
		const errorMessage = 'Unable to audit process. Please try again later.';
		setDisableAuditButton(true);

		ImmapApi.createProcess(processId, true)
			.then(response => {
				if (response.data && response.data && response.data.errors) {
					setDisableAuditButton(false);
					showSnackbar({
						message: response.data.errors[0].message || errorMessage,
						variant: 'error',
					});
				} else if (response.status === 202) {
					getImportData();
					showSnackbar({
						message: fileName + ' is being saved to database',
						variant: 'confirmation',
					});
					setIsAudit(true);
				} else {
					setDisableAuditButton(false);
					showSnackbar({ message: errorMessage, variant: 'error' });
				}
			})
			.catch(error => {
				console.log('Error sending to audit');
			})
			.finally(() => setTimeout(() => onClose(), 2000));
	};

	const handleRemoveImportConfirmation = () => {
		setIsRemoveImportDialogOpen(false);

		ProcessApi.deleteProcess(processId)
			.then(() => {
				getImportData();
				showSnackbar({ message: 'Import removed', variant: 'confirmation' });
				setTimeout(() => onClose(), 2000);
			})
			.catch(error => {
				let errorMessage =
					error.response && error.response.data && error.response.data.errors
						? error.response.data.errors[0].message
						: 'Unable to remove Import. Please try again later.';
				showSnackbar({ message: errorMessage, variant: 'error' });
			});
	};

	useEffect(() => {
		if (isEditGlobalTemplate) {
			setIsEditGlobalTemplate(false);
			getMapAndPremapGlobalTemplateEdit();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditGlobalTemplate]);

	const renderButtons = (processStatus: number): JSX.Element => {
		switch (processStatus) {
			case isAudit === true && IMPORT_PROCESS_STATE.COMPLETE:
			case isAudit === true && IMPORT_PROCESS_STATE.COMPLETE_WITH_ERRORS:
			case IMPORT_PROCESS_STATE.MAX_LIMIT_EXCEEDED:
				return (
					<>
						<SovosIconButton
							tooltipText='Close'
							onClick={() => {
								setGlobalColumnsToHide([]);
								setIsPreparingMapping(false);
								onClose();
							}}
						>
							<Close />
						</SovosIconButton>
					</>
				);
			case IMPORT_PROCESS_STATE.UPLOADING:
				return (
					<>
						<SovosIconButton
							tooltipText='Close'
							onClick={() => {
								deleteFile(fileId);
								setGlobalColumnsToHide([]);
								setIsPreparingMapping(false);
								onClose();
							}}
						>
							<Close />
						</SovosIconButton>
					</>
				);
			case IMPORT_PROCESS_STATE.MATCHING:
				return (
					<>
						<SovosIconButton
							tooltipText='Close'
							onClick={() => {
								deleteFile(fileId);
								setGlobalColumnsToHide([]);
								setIsPreparingMapping(false);
								sessionStorage.removeItem(PREVIEW_FILE_ROWS);
								onClose();
							}}
						>
							<Close />
						</SovosIconButton>
					</>
				);
			case IMPORT_PROCESS_STATE.PROCESSING:
			case IMPORT_PROCESS_STATE.COMPLETE:
				return (
					<>
						<SovosButton
							style={{ marginLeft: 10 }}
							onClick={() => setIsAuditDialogOpen(true)}
							disabled={disableAuditButton}
						>
							Save
						</SovosButton>
						<SovosIconButton
							tooltipText='Close'
							onClick={() => {
								onClose();
							}}
						>
							<Close />
						</SovosIconButton>
					</>
				);
			case IMPORT_PROCESS_STATE.COMPLETE_WITH_ERRORS:
				return (
					<>
						<SovosButton
							style={{ marginLeft: 10 }}
							onClick={() => setIsAuditDialogOpen(true)}
							disabled={disableAuditButton}
						>
							Save
						</SovosButton>
						<SovosIconButton
							tooltipText='Close'
							onClick={() => {
								onClose();
							}}
						>
							<Close />
						</SovosIconButton>
					</>
				);
			case IMPORT_PROCESS_STATE.ERROR:
				return (
					<SovosIconButton style={{ marginLeft: 10 }} color='primary' onClick={() => onClose()} tooltipText='Close'>
						<Close />
					</SovosIconButton>
				);
			case IMPORT_PROCESS_STATE.RETRY:
				return (
					<>
						<SovosButton style={{ marginLeft: 10 }} onClick={handleRetryProcessClick}>
							Save
						</SovosButton>
						<SovosIconButton style={{ marginRight: 10 }} variant='text' onClick={() => onClose()} tooltipText='Close'>
							<Close />
						</SovosIconButton>
					</>
				);
			case IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT:
				return (
					<>
						<SovosIconButton
							tooltipText='Close'
							onClick={() => {
								deleteFile(fileId);
								onClose();
								setGlobalColumnsToHide([]);
								setIsPreparingMapping(false);
								sessionStorage.removeItem(PREVIEW_FILE_ROWS);
							}}
						>
							<Close />
						</SovosIconButton>
					</>
				);
			default:
				return <></>;
		}
	};

	useEffect(() => {
		const storedItem = sessionStorage.getItem('selectedTransaction');

		if (!selectedTransaction && storedItem) {
			setSelectedTransaction(JSON.parse(storedItem));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const storedProcessId = sessionStorage.getItem('processId');

		if (transactionDataExists) {
			sessionStorage.setItem('selectedTransaction', JSON.stringify(selectedTransaction));
			setProcessId(selectedTransaction.processId);
			setIsAudit(selectedTransaction.isAudit);
			const status = IMPORT_PROCESS_STATE[selectedTransaction.processingStatus as keyof typeof IMPORT_PROCESS_STATE];
			setProcessStatus(status);
		} else if (storedProcessId) {
			setProcessId(storedProcessId);
			setProcessStatus(IMPORT_PROCESS_STATE.PROCESSING);
		} else {
			handleAddNewTransaction();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [transactionDataExists, selectedTransaction]);

	useEffect(() => {
		setDisableAuditButton(
			(processStatus !== IMPORT_PROCESS_STATE.COMPLETE &&
				processStatus !== IMPORT_PROCESS_STATE.COMPLETE_WITH_ERRORS) ||
				(selectedTransaction ? selectedTransaction.isAudit : false)
		);
	}, [processStatus, selectedTransaction]);

	useEffect(() => {
		let importTemplate = { ...customImportTemplate };

		if (
			selectedTransaction &&
			selectedTransaction.profileName &&
			selectedTransaction.profileName.substring(9, 10) !== '-'
		) {
			importTemplate.id = selectedTransaction.profileId;
			importTemplate.name = selectedTransaction.profileName;
		} else if (selectedTemplate && selectedTemplate.name && selectedTemplate.name !== CUSTOM_IMPORT) {
			if (selectedCustomTemplate && selectedCustomTemplate.name) {
				importTemplate.id = selectedCustomTemplate.id;
				importTemplate.name = selectedCustomTemplate.name;
			} else {
				importTemplate.id = selectedTemplate.id;
				importTemplate.name = selectedTemplate.name;
			}
		} else if (selectedCustomTemplate && selectedCustomTemplate.name) {
			importTemplate.id = selectedCustomTemplate.id;
			importTemplate.name = selectedCustomTemplate.name;
		}

		setImportTemplate(importTemplate);
	}, [selectedTransaction, selectedCustomTemplate, selectedTemplate]);

	return (
		<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
			<div style={{ width: '100%', maxWidth: 1216 }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 15px 10px 15px' }}>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img
							src={SovosLogo}
							alt='sovos logo'
							style={{ width: 'auto', height: 40, marginRight: '20px', cursor: 'pointer' }}
							onClick={() => onClose()}
						/>
						<span style={{ fontSize: '18px', fontWeight: 500 }}>{isAudit ? fileName : 'Upload File'}</span>
					</div>
					<span>{renderButtons(processStatus)}</span>
				</div>
			</div>
			<div style={{ width: '100%' }}>
				<SovosDivider />
			</div>

			<div style={{ width: '100%', maxWidth: 1216 }}>
				<ImportPageContent
					processStatus={processStatus}
					setProcessStatus={setProcessStatus}
					selectedTemplate={selectedTemplate}
					setSelectedTemplate={setSelectedTemplate}
					processId={processId}
					setProcessId={setProcessId}
					mappedColumns={mappedColumns}
					setMappedColumns={setColumns}
					allTemplatesFetched={allTemplatesFetched}
					setAllTemplatesFetched={setAllTemplatesFetched}
					selectedCustomTemplate={selectedCustomTemplate}
					setSelectedCustomTemplate={setSelectedCustomTemplate}
					advancedOptions={advancedOptions}
					setAdvancedOptions={setAdvancedOptions}
					importTemplate={importTemplate}
					checkboxSelected={checkboxSelected}
					setCheckboxSelected={setCheckboxSelected}
					columnCardsToHide={globalColumnsToHide}
					globalMappingData={globalMappingData}
					isAudit={isAudit}
					onPreviewFail={newHaveExtraRows => setHaveExtraRows(newHaveExtraRows)}
					onDisableBeginProcessingButton={isDisable => setDisableBeginProcessingButton(isDisable)}
					onFetchingTargets={setFetchingTargets}
					onGlobalTemplateEditSelect={handleGlobalTemplateEditSelect}
					columnsHeaders={columnsHeaders}
					setColumnsHeaders={setColumnsHeaders}
					handleUploadStepClick={handleUploadStepClick}
					isPreparingMapping={isPreparingMapping}
					isFetchingTargets={isFetchingTargets}
					handleMatchingStepClick={handleMatchingStepClick}
					isDisableBeginProcessingButton={isDisableBeginProcessingButton}
					haveExtraRows={haveExtraRows}
				/>
			</div>
			<GenericDialog
				open={isAuditDialogOpen}
				title={'Audit this transaction?'}
				message={`All valid transactions included in this file will be saved to the database and made available for reporting and filing.
          This action can not be undone. Are you sure you want to continue?`}
				confirmLabel={'SAVE FOR FILING'}
				onConfirm={() => {
					setIsAuditDialogOpen(false);
					sendToAudit();
				}}
				onCancel={() => setIsAuditDialogOpen(false)}
			/>
			<GenericDialog
				open={isRemoveImportDialogOpen}
				title={'Remove this Import?'}
				message={`Transaction data contained in this import will no longer be available for filing.
          This action cannot be undone.`}
				confirmLabel={'REMOVE IMPORT'}
				isRedConfirmLabel
				onConfirm={handleRemoveImportConfirmation}
				onCancel={() => setIsRemoveImportDialogOpen(false)}
			/>
		</div>
	);
};

export default withSnackbar(ImportPage);
